import { LogOut, MessageCircle, RefreshCw, ShieldOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/lib/store/auth-enhanced";

interface SalonSuspendedScreenProps {
  reason: string | null;
  contactWhatsApp?: string | null;
}

/** Build a wa.me URL from a phone string (digits incl. country code, no "+"). */
function buildWhatsAppUrl(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 6) return null;
  return `https://wa.me/${digits}`;
}

export function SalonSuspendedScreen({ reason, contactWhatsApp }: SalonSuspendedScreenProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const whatsAppUrl = buildWhatsAppUrl(contactWhatsApp);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-6 max-w-md px-6 text-center">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100">
          <ShieldOff className="w-8 h-8 text-red-600" />
        </div>
        <h1 className="text-2xl font-semibold text-gray-900">{t("suspended.heading")}</h1>
        {reason && (
          <div className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
              {t("suspended.reasonLabel")}
            </p>
            <p className="text-sm text-gray-700">{reason}</p>
          </div>
        )}
        {whatsAppUrl && (
          <a
            href={whatsAppUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#1ebe57] transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            {t("suspended.contactWhatsApp")}
          </a>
        )}
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate("/salon-selector")}>
            <RefreshCw className="w-4 h-4" />
            {t("suspended.switchSalon")}
          </Button>
          <Button variant="outline" onClick={() => logout("Logged out from suspended screen")}>
            <LogOut className="w-4 h-4" />
            {t("suspended.logout")}
          </Button>
        </div>
      </div>
    </div>
  );
}
